import { textDirection, truncateLabel } from '../graph';
import { escapeHtml } from '../security';
import { PRINT_COLORS } from '../snapshots';

// Shared pieces of the full-report figures: a standalone SVG frame in the print palette (white page, design tokens
// only), labels that keep their own direction (so Arabic names read correctly), wrapped notes and the source line
// every figure may end with. Pure, so the same builders run in the browser (the document page) and in the Worker
// (the export), and the picture is the same in both.

export const FIGURE_WIDTH = 760;
export const FIGURE_FONT = '"Noto Sans", "Noto Naskh Arabic", "Geeza Pro", Arial, sans-serif';

export const esc = escapeHtml;
export const n1 = (v: number) => (Math.round(v * 10) / 10).toFixed(1);

export interface LabelOpts {
  size?: number;
  weight?: number;
  fill?: string;
  max?: number;
  halo?: boolean;
  /** Which side of `x` the text runs to: `right` starts at x, `left` ends at x, `center` is centered on it. */
  align?: 'right' | 'left' | 'center';
}

/** A label as SVG text in its own direction, placed on the side of `x` that `align` says whatever its script. */
export function svgLabel(text: string, x: number, y: number, opts: LabelOpts = {}): string {
  const t = truncateLabel(text, opts.max ?? 28);
  const dir = textDirection(t);
  const align = opts.align ?? 'right';
  // In a right-to-left label the logical start is its right edge.
  const anchor = align === 'center' ? 'middle' : (align === 'right') === (dir === 'ltr') ? 'start' : 'end';
  return `<text x="${n1(x)}" y="${n1(y)}" direction="${dir}" text-anchor="${anchor}" unicode-bidi="plaintext" dominant-baseline="middle" font-size="${opts.size ?? 11}"${
    opts.weight ? ` font-weight="${opts.weight}"` : ''
  }${opts.halo ? ` paint-order="stroke" stroke="${PRINT_COLORS.background}" stroke-width="3" stroke-linejoin="round"` : ''} fill="${opts.fill ?? PRINT_COLORS.foreground}">${esc(t)}</text>`;
}

/** A standalone SVG document: namespace, role, title, white background. */
export const svgFrame = (w: number, h: number, title: string, body: string) =>
  [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" font-family="${esc(FIGURE_FONT)}" role="img" aria-label="${esc(title)}">`,
    `<title>${esc(title)}</title>`,
    `<rect width="100%" height="100%" fill="${PRINT_COLORS.background}"/>`,
    body,
    '</svg>',
  ].join('\n');

/** An id safe in an attribute without escaping (ids are alphanumeric; anything else is dropped). */
export const attrId = (id: string) => id.replace(/[^0-9A-Za-z_→-]/g, '');

/** Rough advance of one character at 11 px (layout only: labels are truncated, never measured). */
export const CHAR_PX = 6.4;
export const textWidth = (s: string, size = 11) => [...s].length * CHAR_PX * (size / 11);

/** Splits a note into lines of about `max` characters at spaces (at most `lines` lines). */
export function wrapText(text: string, max: number, lines = 6): string[] {
  const out: string[] = [];
  let line = '';
  for (const word of text.trim().split(/\s+/)) {
    if (line && line.length + 1 + word.length > max) {
      out.push(line);
      line = word;
    } else line = line ? `${line} ${word}` : word;
  }
  if (line) out.push(line);
  return out.slice(0, lines);
}

/** Muted footnote lines from `y` (the figure's notes, method and source); returns the markup and its height. */
export function footLines(lines: readonly string[], y: number, x = 20): { svg: string; height: number } {
  const svg = lines.map((l, i) => svgLabel(l, x, y + i * 15, { size: 10, fill: PRINT_COLORS.muted, max: 140 })).join('');
  return { svg, height: lines.length * 15 };
}

/** The figure's title line at the top-left. */
export const figureTitle = (title: string, x = 20, y = 24) => svgLabel(title, x, y, { size: 15, weight: 600, max: 90 });

/** Dash patterns of the typed links schematics and timelines share (solid = function / affiliation). */
export const LINK_DASH = { function: null, funding: '8 5', access: '2 4', procedure: '14 6' } as const;
